import React, { useLayoutEffect, useRef, useState } from 'react';

import { Box, Button } from '@alpha/ui-lib/ui/external';

export interface ToggleOption<T extends string = string> {
  value: T;
  label: React.ReactNode;
}

export interface AnimatedToggleProps<T extends string = string> {
  options: ToggleOption<T>[];
  value: T;
  onChange: (value: T) => void;
  activeColor?: string;
  inactiveColor?: string;
  highlightColor?: string;
  backgroundColor?: string;
}

export const AnimatedToggle = <T extends string = string>({
  options,
  value,
  onChange,
  activeColor = '#1E8777',
  inactiveColor = '#00000066',
  highlightColor = '#1E87771A',
  backgroundColor = '#eee',
}: AnimatedToggleProps<T>): React.ReactElement => {
  const [style, setStyle] = useState({ left: 0, width: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Map<T, HTMLButtonElement>>(new Map());

  useLayoutEffect(() => {
    const activeButton = buttonRefs.current.get(value);

    if (activeButton && containerRef.current) {
      const btn = activeButton.getBoundingClientRect();
      const parent = containerRef.current.getBoundingClientRect();

      setStyle({
        left: btn.left - parent.left,
        width: btn.width,
      });
    }
  }, [value, options]);

  const setButtonRef = (optionValue: T) => (el: HTMLButtonElement | null) => {
    if (el) {
      buttonRefs.current.set(optionValue, el);
    } else {
      buttonRefs.current.delete(optionValue);
    }
  };

  return (
    <Box
      ref={containerRef}
      style={{
        position: 'relative',
        display: 'inline-flex',
        backgroundColor,
        borderRadius: '10px',
        padding: '6px',
        gap: '4px',
        height: '44px',
      }}
    >
      {/* sliding highlight */}
      <Box
        style={{
          position: 'absolute',
          top: 4,
          bottom: 4,
          left: style.left,
          width: style.width,
          backgroundColor: highlightColor,
          borderRadius: '5px',
          transition: 'all 0.3s ease',
          height: '32px',
          marginTop: '1.5px',
        }}
      />

      {options.map((option) => (
        <Button
          key={option.value}
          ref={setButtonRef(option.value)}
          onClick={() => onChange(option.value)}
          style={{
            zIndex: 1,
            borderRadius: '5px',
            textTransform: 'none',
            padding: '8px',
            color: value === option.value ? activeColor : inactiveColor,
            fontSize: '13px',
            height: '32px',
          }}
        >
          {option.label}
        </Button>
      ))}
    </Box>
  );
};

export default AnimatedToggle;
